/**
 * Authentication Guard Component
 *
 * Protects routes by requiring authentication before rendering children.
 * Shows loading state while checking authentication, redirects to sign in
 * if not authenticated.
 *
 * Usage:
 *   <AuthGuard>
 *     <ProtectedContent />
 *   </AuthGuard>
 */

import React from 'react';
import { useAuth } from '../auth';
import { Button, Stack, useTheme, spacing } from '@fti/design-system';

interface AuthGuardProps {
  /** Child components to render when authenticated */
  children: React.ReactNode;
  /** Optional custom loading component */
  loadingComponent?: React.ReactNode;
  /** Optional custom unauthorized component */
  unauthorizedComponent?: React.ReactNode;
}

/**
 * Authentication guard component
 *
 * Checks authentication state and only renders children if user is authenticated.
 * Automatically initiates sign in flow if user is not authenticated.
 *
 * @param props.children - Components to render when authenticated
 * @param props.loadingComponent - Custom loading UI (optional)
 * @param props.unauthorizedComponent - Custom unauthorized UI (optional)
 * @returns Protected content or loading/sign in UI
 */
export function AuthGuard({
  children,
  loadingComponent,
  unauthorizedComponent
}: AuthGuardProps): JSX.Element {
  const auth = useAuth();
  const { theme } = useTheme();

  // Theme-aware colors from design system
  const bgColor = theme.colors.background.default;
  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const primaryColor = theme.colors.primary[500];
  const borderColor = theme.colors.border.default;

  // Show loading state while checking authentication
  if (auth.isLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: bgColor
      }}>
        <Stack align="center" gap="md">
          <div style={{
            width: '40px',
            height: '40px',
            border: `4px solid ${borderColor}`,
            borderTop: `4px solid ${primaryColor}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: mutedColor }}>Checking authentication...</p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </Stack>
      </div>
    );
  }

  // Show unauthorized state if not authenticated
  if (!auth.isAuthenticated) {
    if (unauthorizedComponent) {
      return <>{unauthorizedComponent}</>;
    }

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: bgColor
      }}>
        <Stack align="center" gap="lg" style={{ padding: spacing.xl, maxWidth: '400px', textAlign: 'center' }}>
          <h2 style={{ color: textColor, marginTop: 0 }}>Authentication Required</h2>
          <p style={{ color: mutedColor, marginBottom: spacing.md }}>
            You need to sign in to access this content.
          </p>
          <Button
            variant="solid"
            color="primary"
            onClick={() => auth.signinRedirect()}
          >
            Sign In
          </Button>
        </Stack>
      </div>
    );
  }

  // User is authenticated, render protected content
  return <>{children}</>;
}
